/**
 * CalendarMonthSubcolumnProvider — the typed-aggregator extension point
 * for the grid (calendar-month) layout, mirroring `SubcolumnProvider<H>`
 * on the timeline path.
 *
 * `CalendarListAliasPlugin` holds a registry of these and, for each
 * calendar-month query, asks every provider "do you produce a subcolumn
 * for this row?" — first non-null wins. Today there's one provider
 * (`eventCalendarMonthSubcolumnProvider`); a future task/weather grid
 * renderer registers a sibling and the aggregator picks it up with no
 * aggregator changes. Part of converging the `*.event` renderers onto
 * one strategy — see the calendar-month-multicalendar-events project doc.
 */

import type { Option } from "@tento-lona/sheets";
import type { SheetRow } from "@tento-lona";
import type { CalendarMonthSubcolumn } from "@tento-lona/sheets-ui/sheet/calendar-month-subcolumn.ts";

import {
  EventCalendarMonthSubcolumn,
  type EventCalendarMonthHost,
} from "./build-event-calendar-month-subcolumn.ts";

/** Generic provider contract. The host shape varies per renderer (event
 *  subcolumns need an events lookup; task subcolumns would need a task
 *  lookup, etc.); each provider declares its own. */
export interface CalendarMonthSubcolumnProvider<H> {
  /**
   * Inspect `row` and return a subcolumn iff this provider handles the
   * row's cell shape, else null. Today's only provider is event-shape
   * agnostic at the aggregator gate; future providers add real
   * shape-probe gating.
   */
  subcolumnFor(
    row: SheetRow.Joined.Handle,
    host: H,
  ): Option<CalendarMonthSubcolumn>;
}

/**
 * The canonical event subcolumn provider for the calendar-month layout.
 * The aggregator builds an `EventCalendarMonthHost` (layout context +
 * actions factory + the SDK month read + the list membership) and passes it in.
 */
export const eventCalendarMonthSubcolumnProvider: CalendarMonthSubcolumnProvider<
  EventCalendarMonthHost
> = {
  subcolumnFor(row, host) {
    return EventCalendarMonthSubcolumn.build(row, host);
  },
};
